import { create } from 'zustand';
import type { CommandAckStatus } from '@fabricos/protocol-ts';
import { useFleet } from './fleet';
import { useToasts } from './toasts';

export type UploadStatus = 'pending' | CommandAckStatus;

export interface UploadEntry {
  commandId: string;
  robotId: string;
  startedTs: number;
  waypointCount: number;
  status: UploadStatus;
  message?: string;
}

const TERMINAL: ReadonlySet<CommandAckStatus> = new Set<CommandAckStatus>([
  'completed',
  'failed',
  'cancelled',
  'timeout',
  'rejected',
]);

const IN_FLIGHT_STATES: ReadonlySet<UploadStatus> = new Set<UploadStatus>([
  'pending',
  'accepted',
  'in_progress',
]);

/** Client-side timeout: if no terminal ack arrives in this window, surface a
 *  warn toast so the operator isn't left staring at a stuck spinner. The
 *  vehicle may still complete the upload — we just stop blocking the UI on it. */
const UPLOAD_TIMEOUT_MS = 15_000;

interface MissionUploadStore {
  pending: Record<string, UploadEntry>;
  track: (u: Omit<UploadEntry, 'status'>) => void;
  markStatus: (commandId: string, status: UploadStatus, message?: string | null) => void;
  /** Drop terminal-state entries; useful when the user dismisses the progress UI. */
  clearTerminal: () => void;
  reset: () => void;
}

export const useMissionUpload = create<MissionUploadStore>((set) => ({
  pending: {},
  track: (u) => {
    set((s) => ({
      pending: { ...s.pending, [u.commandId]: { ...u, status: 'pending' } },
    }));
    // Schedule a client-side timeout so the spinner can't get stuck if the
    // Hub or vehicle never sends a terminal ack.
    window.setTimeout(() => {
      const cur = useMissionUpload.getState().pending[u.commandId];
      if (!cur) return;
      if (!IN_FLIGHT_STATES.has(cur.status)) return;
      useMissionUpload.getState().markStatus(u.commandId, 'timeout');
      useToasts.getState().push({
        tone: 'warn',
        title: `Mission upload timed out — ${u.robotId}`,
        body: 'Vehicle did not acknowledge within 15 s. It may still complete in the background.',
        durationMs: 9000,
      });
    }, UPLOAD_TIMEOUT_MS);
  },
  markStatus: (commandId, status, message) =>
    set((s) => {
      const cur = s.pending[commandId];
      if (!cur) return s;
      return {
        pending: {
          ...s.pending,
          [commandId]: { ...cur, status, message: message ?? cur.message },
        },
      };
    }),
  clearTerminal: () =>
    set((s) => {
      const next: Record<string, UploadEntry> = {};
      for (const [k, v] of Object.entries(s.pending)) {
        if (IN_FLIGHT_STATES.has(v.status)) next[k] = v;
      }
      return { pending: next };
    }),
  reset: () => set({ pending: {} }),
}));

/** Mirrors `CommandAck`s on tracked upload commands into the upload store and
 *  emits a toast on terminal status. Installed once at app boot. */
let bridgeInstalled = false;
export function installMissionUploadAckBridge(): void {
  if (bridgeInstalled || typeof window === 'undefined') return;
  bridgeInstalled = true;
  const seen = new Set<string>();
  useFleet.subscribe((state) => {
    for (const ack of state.acks) {
      const key = `${ack.command_id}|${ack.status}|${String(ack.ts_ms)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const tracked = useMissionUpload.getState().pending[ack.command_id];
      if (!tracked) continue;
      useMissionUpload.getState().markStatus(ack.command_id, ack.status, ack.message);
      if (!TERMINAL.has(ack.status)) continue;
      const wp = tracked.waypointCount;
      if (ack.status === 'completed') {
        useToasts.getState().push({
          tone: 'good',
          title: `Mission uploaded — ${tracked.robotId}`,
          body: `${wp} waypoint${wp === 1 ? '' : 's'} accepted by the vehicle.`,
          durationMs: 6000,
        });
      } else {
        useToasts.getState().push({
          tone: ack.status === 'timeout' ? 'warn' : 'critical',
          title: `Mission upload ${ack.status} — ${tracked.robotId}`,
          body: ack.message ?? 'Vehicle did not acknowledge the mission upload.',
          durationMs: 9000,
        });
      }
    }
  });
}
